import { GraphDataViewModel, GraphSelectionModel } from "@features/graph/graphViewModel"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItemText,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { useReactFlow, useStoreApi } from "reactflow"
import { graphNodeClickHandler } from "@features/graph/handlers/graphNodeClick"
import { GraphStoreType } from "@features/graph/Graph"
import { expandGroupNode } from "@features/graph/handlers/graphGroupCollapseExpand"
import ListItem from "@mui/material/ListItem"
import { SyntheticEvent } from "react"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { ParentChildHistory } from "@features/graph/state/parentChildHistory"
import { AddToSelectionButton } from "@features/graph/details/components/AddToSelectionButton"
import { useMatch } from "react-router"
import { GraphNodeType } from "@custom-types/graphs"

export const ParentChildDetails = () => {
  const { fitView, getNode } = useReactFlow()
  const graphStore: GraphStoreType = useStoreApi()
  const [expandedParents, setExpandedParents] = useLocalStorage(
    `nodeParentsAccordionExpanded`,
    true,
  )
  const [expandedChildren, setExpandedChildren] = useLocalStorage(
    `nodeChildrenAccordionExpanded`,
    true,
  )
  const [expandedGroups, setExpandedGroups] = useLocalStorage(`nodeGroupsAccordionExpanded`, true)
  const isResultsPage: boolean = !!useMatch("/Results/Result/:id")

  const showAddToSelectionButton = (id: string) => {
    const node = getNode(id)
    return isResultsPage && node?.type !== GraphNodeType.TABLE && node?.type !== GraphNodeType.GROUP
  }

  const handleGroupClick = (id: string) => {
    graphNodeClickHandler(id, graphStore)
  }

  const handleClick = (id: string) => {
    if (navigateToNode(id)) {
      return
    }
    if (!GraphDataViewModel.data) {
      return
    }

    const node = GraphDataViewModel.data?.nodeMap[id]
    if (!node?.display.groups) {
      return
    }
    // try to expand groups and then navigate
    for (const gid of node.display.groups) {
      const g = GraphDataViewModel.data?.groupMap[gid]
      if (!g) {
        continue
      }
      expandGroupNode(g, GraphDataViewModel.data, graphStore)
    }
    setTimeout(() => navigateToNode(id), 1)
  }

  const navigateToNode = (id: string): boolean => {
    const n = getNode(id)
    if (!n) {
      return false
    }
    if (GraphSelectionModel.selected) {
      ParentChildHistory.push(GraphSelectionModel.selected, id)
    }
    graphNodeClickHandler(id, graphStore)
    setTimeout(() => fitView({ nodes: [n], duration: 0, maxZoom: 2.5 }), 0)
    return true
  }

  const parents = getParentsFromSelected()
  const children = getChildrenFromSelected()
  const groups = getGroupsFromSelected()

  const getItemColor = (n: string) => {
    if (n === ParentChildHistory.backward) return "rgba(0, 0, 255, 0.2)"
    return ""
  }

  return (
    <>
      {parents.length > 0 && (
        <Grid item sx={{ width: expandedParents ? "auto" : "100%" }}>
          <Accordion
            expanded={expandedParents}
            onChange={(event: SyntheticEvent, isExpanded: boolean) => {
              setExpandedParents(isExpanded)
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Parents:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List sx={{ p: 0 }}>
                {parents.map((p) => (
                  <ListItem
                    divider
                    key={p}
                    onClick={() => handleClick(p)}
                    sx={{
                      backgroundColor: getItemColor(p),
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                    }}
                  >
                    <ListItemText primary={getNodeLabel(p)} />
                    {showAddToSelectionButton(p) && (
                      <AddToSelectionButton selectedNode={getNode(p)} />
                    )}
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {children.length > 0 && (
        <Grid item sx={{ width: expandedChildren ? "auto" : "100%" }}>
          <Accordion
            expanded={expandedChildren}
            onChange={(event: SyntheticEvent, isExpanded: boolean) => {
              setExpandedChildren(isExpanded)
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Children:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List sx={{ p: 0 }}>
                {children.map((p) => (
                  <ListItem
                    divider
                    key={p}
                    onClick={() => handleClick(p)}
                    sx={{
                      backgroundColor: getItemColor(p),
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                    }}
                  >
                    <ListItemText primary={getNodeLabel(p)} />
                    {showAddToSelectionButton(p) && (
                      <AddToSelectionButton selectedNode={getNode(p)} />
                    )}
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {groups.length > 0 && (
        <Grid item sx={{ width: expandedGroups ? "auto" : "100%" }}>
          <Accordion
            expanded={expandedGroups}
            onChange={(event: SyntheticEvent, isExpanded: boolean) => {
              setExpandedGroups(isExpanded)
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Groups:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List sx={{ p: 0 }}>
                {groups.map((p) => (
                  <ListItem
                    divider
                    key={p}
                    onClick={() => handleGroupClick(p)}
                    sx={{
                      backgroundColor: getItemColor(p),
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                    }}
                  >
                    <ListItemText primary={getGroupLabel(p)} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </>
  )
}

const getGroupsFromSelected = (): string[] => {
  const out: string[] = []
  if (!GraphSelectionModel.selected) return out
  const node = GraphDataViewModel.data?.nodeMap[GraphSelectionModel.selected]
  if (!node?.display.groups) return out
  return node.display.groups
}

const getNodeLabel = (n: string) => {
  if (n === ParentChildHistory.backward) return `🔙${GraphDataViewModel.data?.labelMap[n]} `
  return GraphDataViewModel.data?.labelMap[n]
}

const getGroupLabel = (gid: string): string => {
  const group = GraphDataViewModel.data?.groupMap[gid]
  if (!group) return ""
  return group.group.label
}

const getParentsFromSelected = (): string[] => {
  if (!GraphSelectionModel.selected) return []
  const node = GraphDataViewModel.data?.nodeMap[GraphSelectionModel.selected]

  if (!node) return []

  const out = new Set<string>()
  node.node?.properties?.parents_lst?.forEach((x) => {
    out.add(x)
  })

  // Table node is selected - it doesn't have parents_lst attribute - need to find all parents manually
  Array.from(GraphSelectionModel.parent).forEach((id) => {
    if (id.startsWith("n")) {
      out.add(id)
    } else {
      // it's group node - try to find if group member attributes include selected table
      GraphDataViewModel.data?.groupMap[id]?.group.nodes.forEach((nid) => {
        if (
          GraphDataViewModel.data?.nodeMap[nid]?.node.properties.attributes_lst?.includes(
            GraphSelectionModel.selected!,
          )
        ) {
          out.add(nid)
        }
      })
    }
  })
  return Array.from(out).sort()
}

const getChildrenFromSelected = (): string[] => {
  if (!GraphSelectionModel.selected) return []
  const out: string[] = []

  Array.from(GraphSelectionModel.child).forEach((id) => {
    if (id.startsWith("n")) {
      out.push(id)
    } else {
      // it's group node - try to find if group member parents include selected node
      GraphDataViewModel.data?.groupMap[id]?.group.nodes.forEach((nid) => {
        if (
          GraphDataViewModel.data?.nodeMap[nid]?.node.properties.parents_lst?.includes(
            GraphSelectionModel.selected!,
          )
        ) {
          out.push(nid)
        }
      })
    }
  })
  return out
}
